import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, File, AlertCircle } from 'lucide-react'
import Button from '../ui/Button'
import ProgressBar from '../ui/ProgressBar'
import { useAudioUpload } from '../../hooks/useAudioUpload'
import { formatFileSize } from '../../utils/formatFileSize'
import { SUPABASE_CONFIG } from '../../services/supabaseClient'

const AudioUploader = ({ onUploadSuccess, onUploadError }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const { uploadFile, isUploading, uploadProgress, error, reset } = useAudioUpload()

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0]
      let errorMessage = '文件上传失败'
      
      if (rejection.errors[0]?.code === 'file-too-large') {
        errorMessage = `文件过大，最大支持 ${formatFileSize(SUPABASE_CONFIG.maxFileSize)}`
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        errorMessage = `不支持的文件格式。支持的格式：MP3、WAV、M4A、AAC、OGG、WebM、FLAC`
      }
      
      onUploadError?.(errorMessage)
      return
    }

    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0])
      reset()
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // 支持所有音频类型的MIME变体
      'audio/mpeg': ['.mp3'],
      'audio/mp3': ['.mp3'],
      'audio/x-mp3': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/x-wav': ['.wav'],
      'audio/wave': ['.wav'],
      'audio/m4a': ['.m4a'],
      'audio/x-m4a': ['.m4a'],
      'audio/mp4': ['.m4a', '.mp4'],
      'audio/mp4a-latm': ['.m4a'],
      'audio/aac': ['.aac'],
      'audio/x-aac': ['.aac'],
      'audio/aacp': ['.aac'],
      'audio/ogg': ['.ogg'],
      'audio/x-ogg': ['.ogg'],
      'audio/webm': ['.webm'],
      'audio/flac': ['.flac'],
      'audio/x-flac': ['.flac']
    },
    multiple: false,
    maxSize: SUPABASE_CONFIG.maxFileSize
  })

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      const result = await uploadFile(selectedFile)
      onUploadSuccess?.(result)
      setSelectedFile(null)
    } catch (err) {
      onUploadError?.(err.message)
    }
  }

  const handleCancel = () => {
    setSelectedFile(null)
    reset()
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* 拖拽上传区域 */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        
        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        
        <div className="space-y-2">
          <p className="text-lg font-medium text-gray-900">
            {isDragActive ? '松开以上传文件' : '拖拽音频文件到此处'}
          </p>
          <p className="text-sm text-gray-500">
            或者 <span className="text-primary-600 font-medium">点击选择文件</span>
          </p>
          <p className="text-xs text-gray-400">
            支持 MP3, WAV, M4A, AAC, OGG, WebM, FLAC 格式，最大 {formatFileSize(SUPABASE_CONFIG.maxFileSize)}
          </p>
        </div>
      </div>

      {/* 文件信息展示 */}
      {selectedFile && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <File className="h-8 w-8 text-gray-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {selectedFile.name}
              </p>
              <p className="text-sm text-gray-500">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            {!isUploading && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
              >
                取消
              </Button>
            )}
          </div>

          {/* 上传进度 */}
          {isUploading && (
            <div className="mt-4">
              <ProgressBar progress={uploadProgress} />
              <p className="text-sm text-gray-600 mt-2">
                正在上传并处理音频文件...
              </p>
            </div>
          )}

          {/* 错误信息 */}
          {error && (
            <div className="mt-4 flex items-center space-x-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* 上传按钮 */}
          {!isUploading && !error && (
            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleUpload}
                loading={isUploading}
                disabled={!selectedFile}
              >
                开始转录
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AudioUploader